import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  Banknote,
  Bell,
  CheckCircle2,
  ChevronDown,
  CreditCard,
  Droplets,
  FlaskConical,
  Factory,
  FileText,
  Landmark,
  Link2,
  MessageCircle,
  Package,
  QrCode,
  ShoppingCart,
  Sparkles,
  Store,
  TrendingUp,
  Users,
} from 'lucide-react';
import PublicSiteShell, { Reveal, registroHref, whatsappHref } from '../components/public/PublicSiteShell';
import { formatPlanPrice } from '../data/publicSite';
import { usePublicPlans } from '../hooks/usePublicPlans';
import { LAUNCH_OFFER, getLaunchLabel, getLaunchPrice, getLaunchSummary, isLaunchOfferActive } from '../data/billingBrandConfig';

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago'];
const SALES = [42, 55, 49, 68, 72, 65, 80, 92];
const BUYS = [30, 38, 36, 44, 48, 50, 56, 60];
const FIN_BARS = [28, 35, 41, 36, 48, 55, 50, 62, 58, 70, 65, 78];
const CAJA_HORAS = ['09', '11', '13', '15', '17', '19'];
const CAJA_COBROS = [34, 56, 46, 72, 60, 88];
const CAJA_PAGOS = [20, 28, 24, 38, 30, 44];

const testimonios = [
  {
    quote: 'Dejamos de perseguir planillas. Ahora sé cuánto tengo en caja antes de cerrar el local.',
    initials: 'VR', name: 'Verónica', role: 'Fábrica de pastas, Rosario',
  },
  {
    quote: 'Con tres sucursales, antes no sabía qué faltaba reponer. Ahora el stock avisa solo.',
    initials: 'FG', name: 'Facundo', role: 'Retail multisucursal, Córdoba',
  },
  {
    quote: 'Empezamos con el plan más chico y escalamos a producción sin cambiar de sistema.',
    initials: 'LP', name: 'Lucía', role: 'Producción de alimentos, CABA',
  },
];

const buildFaqs = (trialDays) => [
  { q: '¿Cómo es el precio de lanzamiento?', a: `${getLaunchSummary(formatPlanPrice)}, para las cuentas creadas hasta el ${LAUNCH_OFFER.untilLabel}. Después cada plan pasa a su precio de lista.` },
  { q: '¿Necesito tarjeta para probar?', a: `No. Empezás con ${trialDays} días gratis sin tarjeta. Pagás recién cuando activás tu plan.` },
  { q: '¿Puedo cambiar de plan más adelante?', a: 'Sí. Podés subir o bajar de plan cuando cambie tu volumen de operaciones, sin perder tu configuración.' },
  { q: '¿Hace falta comprar hardware especial?', a: 'No. Gastify funciona en la nube desde cualquier computadora o tablet con internet.' },
  { q: '¿Qué pasa si supero las operaciones de mi plan?', a: 'Te avisamos antes de que pase para que puedas escalar de plan sin cortes en tu operación.' },
  { q: '¿Gastify funciona para más de un rubro?', a: 'Sí. Se adapta a comercio, gastronomía, indumentaria y producción, entre otros rubros.' },
  { q: '¿Necesito instalar algo?', a: 'No. Gastify es 100% web: entrás desde el navegador, en la computadora o tablet que uses.' },
  { q: '¿Puedo migrar mis datos desde otro sistema?', a: 'Sí, te ayudamos a migrar clientes, productos y stock cuando activás tu cuenta.' },
];

export default function Landing() {
  const navigate = useNavigate();
  const { plans, trialDays, loading: plansLoading } = usePublicPlans();
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <PublicSiteShell
      footerCta={{
        title: 'Creá tu cuenta y ordená tu negocio en minutos.',
        description: isLaunchOfferActive()
          ? `Sin instalaciones, sin tarjeta para probar. ${trialDays} días gratis y después ${getLaunchSummary(formatPlanPrice)}.`
          : `Sin instalaciones, sin tarjeta para probar. ${trialDays} días gratis.`,
        primaryLabel: 'Empezar prueba',
        primaryHref: registroHref,
        secondaryLabel: 'Hablar con un asesor',
        secondaryHref: whatsappHref,
        secondaryExternal: true,
      }}
    >
      {/* ---------- Hero ---------- */}
      <section className="hero">
        <div className="gst-container hero-grid">
          <div>
            <h1>
              <span className="hl">Ordená la operación.</span><br />
              Cobrá con claridad.<br />
              Crecé con rentabilidad.
            </h1>
            <p className="lead">
              Gastify unifica venta, stock, compras y caja en un sistema pensado para negocios
              que ya no pueden seguir manejándose por intuición.
            </p>
            <div className="hero-ctas">
              <Link className="gst-btn gst-btn-primary" to={registroHref}>
                Empezar prueba <ArrowRight size={16} />
              </Link>
              <Link className="gst-btn gst-btn-ghost" to="/precios">
                Ver precios · {trialDays} días de prueba
              </Link>
            </div>
          </div>

          <div className="hero-mock">
            <div className="glow-a" />
            <div className="glow-b" />
            <div className="mock-panel">
              <div className="mock-bar">
                <div className="mock-dots">
                  <span style={{ background: 'var(--gst-peach)' }} />
                  <span style={{ background: 'var(--gst-sand)' }} />
                  <span style={{ background: 'var(--gst-mint-300)' }} />
                </div>
                <div className="mock-tag">Dirección operativa</div>
              </div>
              <div className="mock-body">
                <div className="kpi-row">
                  <div className="kpi" style={{ background: 'var(--gst-surface)' }}>
                    <div className="eyebrow">Ventas</div><div className="val">+26%</div>
                    <p>más claridad en el mostrador</p>
                  </div>
                  <div className="kpi" style={{ background: 'var(--gst-mint-100)' }}>
                    <div className="eyebrow" style={{ color: 'var(--gst-mint-700)' }}>Caja</div>
                    <div className="val">Sin huecos</div><p>cobros y pagos unidos</p>
                  </div>
                  <div className="kpi" style={{ background: 'rgba(254,215,170,0.45)' }}>
                    <div className="eyebrow">Margen</div><div className="val">Visible</div>
                    <p>cuando el negocio madura</p>
                  </div>
                </div>
                <div className="chart-card">
                  <div className="chart-head">
                    <div>
                      <div className="t1">Flujo de fondos · últimos 8 meses</div>
                      <div className="t2">Resultado operativo creciente</div>
                    </div>
                    <div className="legend">
                      <span><i style={{ background: 'var(--gst-mint-500)' }} />Cobros</span>
                      <span><i style={{ background: 'var(--gst-graphite-900)' }} />Pagos</span>
                    </div>
                  </div>
                  <div className="bars">
                    {MONTHS.map((m, i) => (
                      <div className="bar-col" key={m}>
                        <div className="bar-wrap">
                          <div style={{ height: `${SALES[i]}%`, background: 'var(--gst-mint-500)' }} />
                          <div style={{ height: `${BUYS[i]}%`, background: 'var(--gst-graphite-900)' }} />
                        </div>
                        <div className="bar-label">{m}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Pilares ---------- */}
      <section className="gst-section-alt">
        <div className="gst-container" style={{ padding: '48px 0' }}>
          <Reveal className="pillars-head">
            <h2>Una plataforma para operar mejor hoy y dirigir mejor mañana.</h2>
          </Reveal>

          <div className="pillars-list">
            <Reveal className="pillar-row">
              <div className="pillar-text">
                <div className="icon-badge"><Store size={22} /></div>
                <h3>Venta sin fricción</h3>
                <p>Caja, facturación y atención corren en el mismo ritmo para que el equipo cobre rápido sin perder control.</p>
              </div>
              <div className="pillar-mock">
                <div className="pmock-head">
                  <span className="t">Punto de venta</span>
                  <span className="pmock-live"><span />En vivo</span>
                </div>
                <div className="pmock-kpis">
                  <div className="pmock-kpi"><div className="l">Ventas hoy</div><div className="v">$348.200</div></div>
                  <div className="pmock-kpi"><div className="l">Ticket prom.</div><div className="v">$4.850</div></div>
                </div>
                <div className="pmock-tags">
                  <span className="pmock-tag"><Banknote size={12} />Efectivo</span>
                  <span className="pmock-tag"><CreditCard size={12} />Débito</span>
                  <span className="pmock-tag"><QrCode size={12} />QR</span>
                  <span className="pmock-tag"><Users size={12} />Cta. corriente</span>
                </div>
              </div>
            </Reveal>

            <Reveal className="pillar-row rev">
              <div className="pillar-text">
                <div className="icon-badge"><Package size={22} /></div>
                <h3>Stock que sí acompaña</h3>
                <p>Cada venta, compra o producción impacta en tiempo real para que dejes de reponer a ciegas.</p>
              </div>
              <div className="pillar-mock" style={{ position: 'relative' }}>
                <div className="pmock-badge-float">+3 depósitos</div>
                <div className="pmock-head"><span className="t">Stock en tiempo real</span></div>
                <div className="pmock-stockrow">
                  <span className="dot" style={{ background: 'var(--gst-mint-500)' }} />
                  <span className="name">Harina 000 x 25kg</span>
                  <span className="pmock-stockbar"><div style={{ width: '82%', background: 'var(--gst-mint-500)' }} /></span>
                  <span className="pmock-qty">128 u</span>
                </div>
                <div className="pmock-stockrow">
                  <span className="dot" style={{ background: '#DC2626' }} />
                  <span className="name">Aceite de girasol</span>
                  <span className="pmock-stockbar"><div style={{ width: '12%', background: '#DC2626' }} /></span>
                  <span className="pmock-qty">6 u</span>
                </div>
                <div className="pmock-stockrow">
                  <span className="dot" style={{ background: 'var(--gst-mint-500)' }} />
                  <span className="name">Levadura</span>
                  <span className="pmock-stockbar"><div style={{ width: '60%', background: 'var(--gst-mint-500)' }} /></span>
                  <span className="pmock-qty">54 u</span>
                </div>
              </div>
            </Reveal>

            <Reveal className="pillar-row">
              <div className="pillar-text">
                <div className="icon-badge"><Landmark size={22} /></div>
                <h3>Caja con lectura real</h3>
                <p>Cobros, pagos y bancos conectados para entender qué entra, qué sale y cuánto podés mover.</p>
              </div>
              <div className="pillar-mock" style={{ position: 'relative' }}>
                <div className="pmock-badge-float" style={{ background: 'var(--gst-mint-500)', color: 'var(--gst-graphite-900)' }}>+12% vs ayer</div>
                <div className="pmock-head">
                  <span className="t">Caja · hoy</span>
                  <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--gst-graphite-500)' }}>Turno tarde</span>
                </div>
                <div className="pmock-kpis">
                  <div className="pmock-kpi">
                    <div className="l">Entró</div>
                    <div className="v-row">
                      <ArrowDownLeft size={14} style={{ color: 'var(--gst-mint-600)' }} />
                      <span className="v pos">$386.900</span>
                    </div>
                  </div>
                  <div className="pmock-kpi">
                    <div className="l">Salió</div>
                    <div className="v-row">
                      <ArrowUpRight size={14} style={{ color: '#B45309' }} />
                      <span className="v neg">$174.600</span>
                    </div>
                  </div>
                </div>
                <div className="pmock-legend">
                  <span><i style={{ background: 'var(--gst-mint-500)' }} />Cobros</span>
                  <span><i style={{ background: 'var(--gst-graphite-900)' }} />Pagos</span>
                </div>
                <div className="pmock-hbars">
                  {CAJA_HORAS.map((h, i) => (
                    <div className="pmock-hbar-col" key={h}>
                      <div className="pmock-hbar-wrap">
                        <div style={{ height: `${CAJA_COBROS[i]}%`, background: 'var(--gst-mint-500)' }} />
                        <div style={{ height: `${CAJA_PAGOS[i]}%`, background: 'var(--gst-graphite-900)' }} />
                      </div>
                      <div className="pmock-hbar-label">{h}</div>
                    </div>
                  ))}
                </div>
                <div className="pmock-stat">
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--gst-graphite-700)' }}>Disponible ahora</span>
                  <span style={{ fontSize: 18, fontWeight: 900, color: 'var(--gst-graphite-900)' }}>$412.300</span>
                </div>
              </div>
            </Reveal>

            <Reveal className="pillar-row rev">
              <div className="pillar-text">
                <div className="icon-badge"><Factory size={22} /></div>
                <h3>Producción con costo real</h3>
                <p>Armá recetas y descontá insumos automáticamente: cada unidad producida sale con su costo real, no con una estimación.</p>
              </div>
              <div className="pillar-mock" style={{ position: 'relative' }}>
                <div className="pmock-badge-float">$274,50 / unidad</div>
                <div className="pmock-head"><span className="t">Receta · Pan de campo x 40u</span></div>
                <div className="pmock-introw">
                  <span className="chip"><Package size={15} /></span>
                  <span className="name">Harina 000 · 12 kg</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--gst-graphite-700)' }}>$9.600</span>
                </div>
                <div className="pmock-introw">
                  <span className="chip"><FlaskConical size={15} /></span>
                  <span className="name">Levadura · 300 g</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--gst-graphite-700)' }}>$1.200</span>
                </div>
                <div className="pmock-introw">
                  <span className="chip"><Droplets size={15} /></span>
                  <span className="name">Sal · 200 g</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--gst-graphite-700)' }}>$180</span>
                </div>
              </div>
            </Reveal>

            <Reveal className="pillar-row">
              <div className="pillar-text">
                <div className="icon-badge"><TrendingUp size={22} /></div>
                <h3>Decisiones con margen</h3>
                <p>Tomá decisiones con información. Analizá tu rentabilidad, flujo de fondos o producción.</p>
              </div>
              <div className="pillar-mock">
                <div className="pmock-head">
                  <span className="t">Ranking de productos</span>
                  <TrendingUp size={16} style={{ color: 'var(--gst-mint-600)' }} />
                </div>
                <div className="pmock-rank"><span className="pmock-medal">1</span><span className="nm">Combo desayuno</span><span className="pct">+18%</span></div>
                <div className="pmock-rank"><span className="pmock-medal">2</span><span className="nm">Pan de campo</span><span className="pct">+12%</span></div>
                <div className="pmock-rank"><span className="pmock-medal">3</span><span className="nm">Café de especialidad</span><span className="pct">+9%</span></div>
              </div>
            </Reveal>

            <Reveal className="pillar-row rev">
              <div className="pillar-text">
                <div className="icon-badge"><Link2 size={22} /></div>
                <h3>Integraciones que ahorran tiempo</h3>
                <p>Conectá Gastify con AFIP/ARCA, Mercado Libre y Tiendanube para que la información viaje sola entre plataformas, sin cargar nada a mano.</p>
                <Link className="gst-btn gst-btn-ghost gst-btn-sm" to="/funcionalidades/integraciones" style={{ marginTop: 24 }}>
                  Ver integraciones <ArrowRight size={14} />
                </Link>
              </div>
              <div className="pillar-mock">
                <div className="pmock-head"><span className="t">Integraciones</span></div>
                <div className="pmock-introw"><span className="chip"><FileText size={15} /></span><span className="name">Facturación AFIP/ARCA</span><div className="pmock-toggle" /></div>
                <div className="pmock-introw"><span className="chip"><ShoppingCart size={15} /></span><span className="name">Mercado Libre</span><div className="pmock-toggle" /></div>
                <div className="pmock-introw"><span className="chip"><Store size={15} /></span><span className="name">Tiendanube</span><div className="pmock-toggle" /></div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- Planes ---------- */}
      <section className="gst-container plans-section" id="planes">
        {isLaunchOfferActive() && (
          <Reveal className="promo-strip">
            <Sparkles size={18} style={{ color: 'var(--gst-mint-200)' }} />
            <span><strong>Lanzamiento:</strong> {getLaunchSummary(formatPlanPrice)}, para altas hasta el {LAUNCH_OFFER.untilLabel} · {trialDays} días de prueba gratis, sin tarjeta.</span>
          </Reveal>
        )}
        <Reveal className="plans-head">
          <h2>Elegí según cuánto vendas, no según lo que puedas pagar.</h2>
          <Link className="gst-btn gst-btn-ghost gst-btn-sm" to="/precios">
            Ver todos los detalles <ArrowRight size={14} />
          </Link>
        </Reveal>
        <div className="plans-grid">
          {plans.map((plan, index) => {
            const featured = index === 1;
            const promo = getLaunchPrice(plan.key, plan.price_monthly);
            return (
              <article className={`gst-plan${featured ? ' featured' : ''}`} key={plan.key}>
                {featured && <div className="gst-plan-ribbon">Más elegido</div>}
                <div className="gst-eyebrow">{plan.badge}</div>
                <h3 style={{ marginTop: 14, fontSize: 22, fontWeight: 900 }}>{plan.marketingName}</h3>
                <p style={{ marginTop: 8, fontSize: 13, lineHeight: 1.5, color: 'var(--gst-graphite-700)' }}>
                  {plan.audience || plan.summary}
                </p>
                <div style={{ marginTop: 20 }}>
                  {plansLoading || plan.price_monthly <= 0 ? (
                    <span className="shimmer" style={{ width: 160, height: 34, borderRadius: 10 }} />
                  ) : (
                    promo ? (
                      <>
                        <span className="gst-plan-price-old">{formatPlanPrice(plan.price_monthly)}</span>
                        <div className="gst-plan-price">{formatPlanPrice(promo)}<small>/mes + IVA</small></div>
                      </>
                    ) : (
                      <div className="gst-plan-price">{formatPlanPrice(plan.price_monthly)}<small>/mes + IVA</small></div>
                    )
                  )}
                </div>
                {promo && <div className="promo-note">{getLaunchLabel(plan.key)}</div>}
                <div className="plan-ops">{plan.operationLabel}</div>
                <ul style={{ marginTop: 18, flex: 1 }}>
                  {(plan.highlights || []).slice(0, 4).map((f) => (
                    <li key={f}><CheckCircle2 size={16} />{f}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className={`gst-btn ${featured ? 'gst-btn-primary' : 'gst-btn-ghost'} gst-btn-block`}
                  style={{ marginTop: 20 }}
                  onClick={() => navigate(`${registroHref}?plan=${plan.key}`)}
                >
                  Empezar prueba
                </button>
              </article>
            );
          })}
        </div>
      </section>

      {/* ---------- Split ---------- */}
      <section className="gst-section-alt">
        <div className="gst-container">
          <div className="split-grid">
            <Reveal className="split-card" as="article">
              <div className="split-head">
                <div className="eyebrow">Stock, compras y reposición</div>
                <h3>Control visible para equipos que se mueven rápido.</h3>
              </div>
              <div className="split-body" style={{ position: 'relative', paddingBottom: 90 }}>
                <div className="stock-mock">
                  <table>
                    <thead>
                      <tr><th>Producto</th><th style={{ textAlign: 'right' }}>Stock</th><th style={{ textAlign: 'right' }}>Estado</th></tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Yerba mate 1 kg</td><td style={{ textAlign: 'right' }}>128</td>
                        <td style={{ textAlign: 'right' }}><span className="stock-tag" style={{ background: 'var(--gst-mint-100)', color: 'var(--gst-mint-700)' }}>OK</span></td>
                      </tr>
                      <tr>
                        <td>Galletitas surtidas</td><td style={{ textAlign: 'right' }}>14</td>
                        <td style={{ textAlign: 'right' }}><span className="stock-tag" style={{ background: '#FEE2E2', color: '#991B1B' }}>Crítico</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mock-float" style={{ bottom: 12, right: -10, width: 150 }}>
                  <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--gst-graphite-500)' }}>Alerta de stock</div>
                  <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Bell size={14} style={{ color: '#991B1B' }} />
                    <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--gst-graphite-900)' }}>2 productos críticos</span>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal className="split-card dark" as="article">
              <div className="split-head">
                <div className="eyebrow">Caja, bancos y proyección</div>
                <h3>Cuando el negocio madura, necesitás mirar la plata con otra profundidad.</h3>
              </div>
              <div className="split-body" style={{ position: 'relative', paddingBottom: 80 }}>
                <div className="fin-mock">
                  <div className="fin-bars">
                    {FIN_BARS.map((v, i) => (
                      <div key={i} style={{ height: `${v}%`, background: i > 8 ? 'var(--gst-mint-400)' : 'rgba(255,255,255,0.4)' }} />
                    ))}
                  </div>
                  <div className="fin-result">
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--gst-mint-200)' }}>Resultado proyectado</div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: '#fff', marginTop: 4 }}>+ $ 1.842.300</div>
                    </div>
                    <span style={{ display: 'inline-flex', padding: '4px 10px', borderRadius: 9999, background: 'var(--gst-mint-500)', color: '#fff', fontSize: 11, fontWeight: 900, letterSpacing: '.1em', textTransform: 'uppercase' }}>+34%</span>
                  </div>
                </div>
                <div className="mock-float" style={{ bottom: 14, left: 16, background: 'var(--gst-graphite-900)', borderColor: 'rgba(255,255,255,0.15)' }}>
                  <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--gst-mint-200)' }}>Cobrado hoy</div>
                  <div style={{ marginTop: 4, fontSize: 15, fontWeight: 900, color: '#fff' }}>$186.400</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- Testimonios ---------- */}
      <section className="gst-container" style={{ paddingTop: 56 }}>
        <Reveal className="pillars-head">
          <h2 style={{ fontSize: 'clamp(28px,3.6vw,40px)' }}>Comercios reales, resultados reales.</h2>
        </Reveal>
        <div className="testi-grid">
          {testimonios.map((t) => (
            <Reveal className="testi-card" key={t.name}>
              <p>“{t.quote}”</p>
              <div className="testi-person">
                <div className="testi-avatar">{t.initials}</div>
                <div>
                  <div className="name">{t.name}</div>
                  <div className="role">{t.role}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section className="gst-section-alt">
        <div className="gst-container faq-section">
          <Reveal className="faq-head">
            <div className="gst-eyebrow gst-eyebrow-mint" style={{ marginBottom: 12 }}>Preguntas frecuentes</div>
            <h2>Todo lo que necesitás saber antes de empezar.</h2>
          </Reveal>
          <div>
            {buildFaqs(trialDays).map((f, i) => (
              <div className={`faq-item${openFaq === i ? ' open' : ''}`} key={f.q}>
                <button type="button" className="faq-q" onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                  <h4>{f.q}</h4><ChevronDown size={20} />
                </button>
                <div className="faq-a"><p>{f.a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="gst-container" style={{ display: 'flex', justifyContent: 'center', padding: '32px 0 0' }}>
        <a className="gst-btn gst-btn-ghost gst-btn-sm" href={whatsappHref} target="_blank" rel="noopener noreferrer">
          <MessageCircle size={16} /> Coordinar una demo por WhatsApp
        </a>
      </div>
    </PublicSiteShell>
  );
}
